import { z } from 'zod';
import {
  OfferVersionIdSchema,
  OfferVersionStatusSchema,
  OfferSnapshotUnitSchema,
  OfferVersionWithSnapshotSchema,
} from './offer-version.js';
import { OfferIdSchema } from './offer.js';
import { AvailabilityPeriodCellSchema } from './availability.js';
import {
  UnitDecisionsArraySchema,
  CarryForwardPrefillSchema,
  RecordedUnitDecisionsSchema,
} from './offer-version-unit-decisions.js';
import { BatchReserveCellSchema } from './reservation-batch.js';
import { ApiErrorCode } from '../api/error-codes.js';

// ─── Internal diff row (full field-level detail for internal DiffPanel) ───────

/**
 * Represents a single row in the internal version diff.
 * changeType 'added' | 'removed': no field/previousValue/newValue.
 * changeType 'changed': field is the API identifier ('clientPriceNet' | 'discountPercent'
 * | 'dateFrom' | 'dateTo' | string), previousValue and newValue are string representations.
 *
 * Identification fields (CU-86cb4wky5): human-readable unit identity sourced from the
 * version snapshot the row belongs to (newer snapshot for added/changed, older for
 * removed) so the diff panel does not have to key off the raw unitId. `supplierName`
 * and `supplierNumber` are subject to server-side `supplier.view` redaction — a caller
 * without an unscoped grant receives both as null while the other fields stay populated.
 */
export const OfferVersionDiffRowSchema = z.object({
  unitId: z.string().min(1),
  changeType: z.enum(['added', 'removed', 'changed']),
  /** API identifier for the changed field; undefined for added/removed rows. */
  field: z.string().optional(),
  previousValue: z.string().optional(),
  newValue: z.string().optional(),
  supplierName: z.string().nullable().optional(),
  supplierNumber: z.string().nullable().optional(),
  /** RW-Group display number (grwId). */
  mediaUnitNumber: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
});
export type OfferVersionDiffRow = z.infer<typeof OfferVersionDiffRowSchema>;

// ─── Diff response (internal endpoint) ────────────────────────────────────────

export const OfferVersionDiffResponseSchema = z.object({
  rows: z.array(OfferVersionDiffRowSchema),
});
export type OfferVersionDiffResponse = z.infer<typeof OfferVersionDiffResponseSchema>;

// ─── Client diff item (simplified shape for WhatChangedPanel) ─────────────────

/**
 * Simplified diff item consumed by the client-facing WhatChangedPanel.
 * Matches the Phase 0 PR #397 `DiffItem {type, unitId, description}` shape.
 * Extended with optional supplier/GRW identifiers (CU-86ca94drx):
 * - supplierNumber + supplierName: the server does NOT populate these for
 *   client-facing responses (getClientView / public client-view) as of
 *   CU-86cayh4t1 — clients must not see supplier identity. The fields stay in
 *   the schema (both already `.nullable().optional()`) only because the
 *   internal diff endpoint still uses this simplified shape's sibling
 *   concept; no client-facing caller receives a non-null value here.
 * - grwId: always present; the client-facing identifier used in place of
 *   supplier identity — WhatChangedPanel falls back to it.
 */
export const ClientDiffItemSchema = z.object({
  type: z.enum(['added', 'removed', 'changed']),
  unitId: z.string().min(1),
  description: z.string(),
  supplierNumber: z.string().nullable().optional(),
  supplierName: z.string().nullable().optional(),
  grwId: z.string().nullable().optional(),
});
export type ClientDiffItem = z.infer<typeof ClientDiffItemSchema>;

// ─── Shared client-facing version fields ──────────────────────────────────────

/**
 * Shared .extend() fields applied to every client-facing version schema.
 * Extracted to prevent silent divergence between OfferVersionSummaryForClientSchema
 * and AcceptVersionResponseSchema.
 */
const offerVersionClientFields = {
  id: OfferVersionIdSchema,
  status: OfferVersionStatusSchema,
  publishedAt: z.coerce.date(),
  acceptedAt: z.coerce.date().nullable().optional(),
  /** Set when the client rejected this version. Does not change `status`. */
  rejectedAt: z.coerce.date().nullable().optional(),
} as const;

// ─── Hand-authored base for client-facing version schemas ─────────────────────

const OfferVersionClientBaseSchema = z.object({
  id: z.number(),
  versionNumber: z.number().int(),
  status: OfferVersionStatusSchema,
  publishedAt: z.date(),
  acceptedAt: z.date().nullable(),
});

// ─── Client offer view response ────────────────────────────────────────────────

/**
 * Response from GET /offers/:id/client-view.
 * version: the latest PUBLISHED version summary.
 * diff: null on first view (or same version re-view); DiffItem[] when a newer
 *       version has been published since the caller's last view.
 */
export const OfferVersionSummaryForClientSchema =
  OfferVersionClientBaseSchema.extend(offerVersionClientFields);
export type OfferVersionSummaryForClient = z.infer<typeof OfferVersionSummaryForClientSchema>;

// ─── Client offer view unit ────────────────────────────────────────────────────

/**
 * A single unit in the authenticated client offer view response.
 * Extends OfferSnapshotUnitSchema with optional live-enriched display fields
 * (coordinates, physical dimensions, signed photo URL) and a client-projected
 * availability period set.
 * Note: Intentionally extends OfferSnapshotUnitSchema directly (not PublicOfferViewUnitSchema)
 * to avoid a circular import — offer-public-link.ts already imports from this file.
 */
export const ClientOfferViewUnitSchema = OfferSnapshotUnitSchema.extend({
  longitude: z.number().nullable(),
  latitude: z.number().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  // Server-resolved rendition URLs (media-photo-derivatives), replacing photoUrl.
  // Null when the live media unit/photo is absent — see photoUrl's prior contract.
  thumbnailUrl: z.string().nullable(),
  displayUrl: z.string().nullable(),
  /** Client-visible free-text location description, live-resolved from media_units.location_description. */
  locationDescription: z.string().nullable(),
  availability: z.array(AvailabilityPeriodCellSchema),
});
export type ClientOfferViewUnit = z.infer<typeof ClientOfferViewUnitSchema>;

export const ClientOfferViewResponseSchema = z.object({
  version: OfferVersionSummaryForClientSchema,
  diff: z.array(ClientDiffItemSchema).nullable(),
  units: z.array(ClientOfferViewUnitSchema),
  validUntil: z.coerce.date(),
  /**
   * Offer-level campaign range, used by the client to fall back a never-reserved
   * unit's displayed period instead of "today–today" (CU-86caypx6r).
   */
  defaultDateFrom: z.coerce.date().nullable(),
  defaultDateTo: z.coerce.date().nullable(),
  /**
   * Top-level sibling of `version` (NOT nested under it) — the client's
   * carried-over per-unit accept/period pre-fill (offer-decision-carry-forward).
   * Required, non-optional: an empty pre-fill is `[]`, never omitted.
   */
  carryForwardPrefill: CarryForwardPrefillSchema,
  /**
   * Top-level sibling of `version` (NOT nested under it) — the per-unit
   * decisions recorded when this version was accepted
   * (client-offer-decision-persistence). Optional, unlike carryForwardPrefill:
   * an older api deployment omits this key entirely, and that MUST still
   * parse so a rollback does not break the whole client offer page. Absent
   * for a version that is not accepted; an empty array for an accepted
   * version with no recorded decisions.
   */
  recordedDecisions: RecordedUnitDecisionsSchema.optional(),
});
export type ClientOfferViewResponse = z.infer<typeof ClientOfferViewResponseSchema>;

// ─── Version snapshot with live enrichment (internal preview endpoint) ────────

/**
 * Response shape for GET /offers/:id/versions/:versionId (the internal, authenticated
 * version-snapshot preview used by the "podgląd" action on the Versions tab).
 *
 * `snapshotData.units` stays the frozen historical record (unchanged). `enrichedUnits`
 * is a sibling array — live coordinates, physical dimensions, a signed photo URL, and
 * client-projected availability, joined by unit id — so this preview shows the same
 * map pins as the authenticated (/offers/:offerId/client) and public
 * (/public/offers/:token) client offer views instead of always nulling coordinates.
 * Mirrors `ClientOfferViewResponseSchema.units` (same `ClientOfferViewUnitSchema` shape,
 * same enrichment source — `OfferVersionsService.enrichSnapshotUnits`).
 */
export const OfferVersionSnapshotWithEnrichmentSchema = OfferVersionWithSnapshotSchema.extend({
  enrichedUnits: z.array(ClientOfferViewUnitSchema),
});
export type OfferVersionSnapshotWithEnrichment = z.infer<
  typeof OfferVersionSnapshotWithEnrichmentSchema
>;

// ─── Accept version request body ──────────────────────────────────────────────

/**
 * Body for POST /offers/:id/versions/:versionId/accept — no required fields.
 * unitDecisions: optional per-unit accept/reject decision payload, submitted
 * once at whole-offer finalize time (client-offer-decision-persistence).
 * Omittable entirely for backward compatibility with pre-capability callers.
 * Whether the payload names at least one ACCEPTED entry is NOT enforced here:
 * the validation pipe answers 400 for every schema failure, but this change
 * specifies 409 for that precondition. It is owned by the two accept
 * endpoints (task 2.1), which check it against the parsed body before the
 * acceptance transaction and refuse with the existing no-accepted-units
 * error code — an omitted or empty `unitDecisions` validates here and is
 * refused there, same as a payload naming no accepted nośnik.
 */
export const AcceptVersionBodySchema = z
  .object({ unitDecisions: UnitDecisionsArraySchema.optional() })
  .strict();
export type AcceptVersionBody = z.infer<typeof AcceptVersionBodySchema>;

// ─── Accept version response ──────────────────────────────────────────────────

/**
 * Response from POST /offers/:id/versions/:versionId/accept.
 * Returns the version row after acceptance (or the existing row on idempotent re-accept).
 * Internal fields (snapshotData, publishedBy, changeDescription, acceptedBy, createdAt)
 * are stripped — only client-safe fields are returned.
 */
export const AcceptVersionResponseSchema =
  OfferVersionClientBaseSchema.extend(offerVersionClientFields);
export type AcceptVersionResponse = z.infer<typeof AcceptVersionResponseSchema>;

// ─── Reject version request body ──────────────────────────────────────────────

/**
 * Body for POST /offers/:id/versions/:versionId/reject and
 * POST /public/offers/:token/reject.
 * reason: optional free-text rejection reason, max 1000 chars.
 * unitDecisions: optional per-unit accept/reject decision payload — a client
 * rejecting the whole offer may still have made real per-unit decisions
 * beforehand, and those must not be discarded (client-offer-decision-persistence).
 */
export const RejectVersionBodySchema = z
  .object({
    reason: z.string().trim().max(1000).optional(),
    unitDecisions: UnitDecisionsArraySchema.optional(),
  })
  .strict();
export type RejectVersionBody = z.infer<typeof RejectVersionBodySchema>;

// ─── Reject version response ──────────────────────────────────────────────────

/**
 * Shape of the reservations released (or that failed to release) by an
 * accepted-version withdrawal — shared by the single-version reject response
 * and the bulk-withdrawal outcome below so the two never drift apart.
 */
const ReservationReleaseOutcomeSchema = z.object({
  released: z.array(z.number().int().positive()),
  failed: z.array(
    z.object({
      reservationId: z.number().int().positive(),
      errorCode: z.literal(ApiErrorCode.RESERVATION_STATUS_BLOCKED),
    }),
  ),
});

/**
 * Response from POST /offers/:id/versions/:versionId/reject (and the public-link variant).
 * Returns the version row after rejection (or the existing row on idempotent re-reject).
 * A PUBLISHED rejection keeps status; an ACCEPTED rejection returns the version
 * to PUBLISHED after withdrawing its acceptance.
 */
export const RejectVersionResponseSchema = OfferVersionClientBaseSchema.extend({
  ...offerVersionClientFields,
  rejectionReason: z.string().nullable().optional(),
  reservationRelease: ReservationReleaseOutcomeSchema.optional(),
});
export type RejectVersionResponse = z.infer<typeof RejectVersionResponseSchema>;

// ─── Acceptance-withdrawal preview (offer-versioning-diff) ─────────────────
//
// Read-only preview of what withdrawing an ACCEPTED version's acceptance
// would release: every reservation locked for that version by the manual
// lock action, identified by nośnik and window, and whether the withdrawal
// would release it or fail to (offer-versioning-diff / "Acceptance-withdrawal
// preview"). GET, path params only — no request schema needed here, same
// convention as ConfirmationPreviewResponseSchema/SalePreviewResponseSchema
// (campaign-sale.ts).

export const WithdrawalPreviewItemSchema = z.discriminatedUnion('outcome', [
  BatchReserveCellSchema.extend({ outcome: z.literal('releasable') }),
  BatchReserveCellSchema.extend({ outcome: z.literal('unresolvable'), reason: z.string() }),
]);
export type WithdrawalPreviewItem = z.infer<typeof WithdrawalPreviewItemSchema>;

export const WithdrawalPreviewResponseSchema = z.array(WithdrawalPreviewItemSchema);
export type WithdrawalPreviewResponse = z.infer<typeof WithdrawalPreviewResponseSchema>;

// ─── Bulk staff withdrawal of acceptance ───────────────────────────────────────
// client-offer-decision-persistence / Bulk staff withdrawal of acceptance across offers

/** Shared cap on how many offer/version pairs one bulk-withdrawal request may carry. */
export const WITHDRAW_ACCEPTANCE_BULK_MAX_ENTRIES = 100;

export const WithdrawAcceptanceBulkEntrySchema = z
  .object({
    offerId: OfferIdSchema,
    versionId: OfferVersionIdSchema,
  })
  .strict();
export type WithdrawAcceptanceBulkEntry = z.infer<typeof WithdrawAcceptanceBulkEntrySchema>;

/**
 * Body for POST /offers/versions/withdraw-acceptance-bulk.
 * versions: non-empty, capped at WITHDRAW_ACCEPTANCE_BULK_MAX_ENTRIES.
 * reason: optional free-text reason applied to every processed entry.
 */
export const WithdrawAcceptanceBulkRequestSchema = z
  .object({
    versions: z
      .array(WithdrawAcceptanceBulkEntrySchema)
      .min(1)
      .max(WITHDRAW_ACCEPTANCE_BULK_MAX_ENTRIES),
    reason: z.string().trim().max(1000).optional(),
  })
  .strict();
export type WithdrawAcceptanceBulkRequest = z.infer<typeof WithdrawAcceptanceBulkRequestSchema>;

/**
 * Per-entry outcome of a bulk withdrawal. 'withdrawn' carries the nested
 * reservation-release outcome so the caller can see exactly what was freed
 * per offer; 'skipped' covers an already-withdrawn or never-accepted version;
 * 'failed' covers authorization failures, a SUPERSEDED version, and any other
 * HttpException raised while processing that entry.
 */
export const WithdrawAcceptanceBulkOutcomeSchema = z.discriminatedUnion('outcome', [
  z.object({
    offerId: OfferIdSchema,
    versionId: OfferVersionIdSchema,
    outcome: z.literal('withdrawn'),
    reservationRelease: ReservationReleaseOutcomeSchema,
  }),
  z.object({
    offerId: OfferIdSchema,
    versionId: OfferVersionIdSchema,
    outcome: z.literal('skipped'),
  }),
  z.object({
    offerId: OfferIdSchema,
    versionId: OfferVersionIdSchema,
    outcome: z.literal('failed'),
    errorCode: z.string(),
  }),
]);
export type WithdrawAcceptanceBulkOutcome = z.infer<typeof WithdrawAcceptanceBulkOutcomeSchema>;

export const WithdrawAcceptanceBulkResponseSchema = z.object({
  results: z.array(WithdrawAcceptanceBulkOutcomeSchema),
});
export type WithdrawAcceptanceBulkResponse = z.infer<typeof WithdrawAcceptanceBulkResponseSchema>;
